'use client';
import React from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';

// Client-only date display
function DateDisplay() {
  const [date, setDate] = React.useState('');
  React.useEffect(() => {
    const fmt = new Intl.DateTimeFormat('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    setDate(fmt.format(new Date()));
  }, []);
  return <span>{date}</span>;
}

// Client-only clock
function TimeDisplay() {
  const [time, setTime] = React.useState('');
  React.useEffect(() => {
    const tick = () => {
      const fmt = new Intl.DateTimeFormat('vi-VN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setTime(fmt.format(new Date()));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return <span>{time}</span>;
}

const Header = (props) => {
  const t = useTranslations('');
  const router = useRouter();
  const { websiteCode } = props;
  const menuTree = props?.menuTree ?? [];

  const handleSearch = (value) => {
    router.push(`/${websiteCode ? `sub/${websiteCode}/` : ''}tim-kiem-tin-tuc?text=${value}`);
  };

  return (
    <div className="w-full bg-[#b71c1c] text-white text-[13px] hidden md:block">
      <div className="max-w-screen-xl mx-auto px-4 flex items-center justify-between h-9">

        {/* Left: Date & Time */}
        <div className="flex items-center gap-3 text-white/80">
          <span className="flex items-center gap-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <DateDisplay />
          </span>
          <span className="opacity-30">|</span>
          <span className="flex items-center gap-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            <TimeDisplay />
          </span>
        </div>

        {/* Right: Search + Nav links */}
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="flex items-center bg-white/15 border border-white/30 rounded overflow-hidden">
            <input
              id="search-input"
              type="text"
              placeholder={t('home.searchArticle') || 'Tìm kiếm...'}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch(e.target.value);
              }}
              className="bg-transparent border-none text-white placeholder-white/60 px-3 py-1 text-[13px] w-44 outline-none"
            />
            <button
              onClick={() => {
                const input = document.getElementById('search-input');
                if (input) handleSearch(input.value);
              }}
              aria-label="Tìm kiếm"
              className="bg-[#f5c518] hover:bg-[#ffea00] text-[#1a3c6e] px-3 py-1.5 transition-colors duration-200 cursor-pointer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 16 16" fill="none">
                <path
                  d="M14.2125 13.3516L10.1547 9.29375C10.7844 8.47969 11.125 7.48438 11.125 6.4375C11.125 5.18438 10.6359 4.00937 9.75156 3.12344C8.86719 2.2375 7.68906 1.75 6.4375 1.75C5.18594 1.75 4.00781 2.23906 3.12344 3.12344C2.2375 4.00781 1.75 5.18438 1.75 6.4375C1.75 7.68906 2.23906 8.86719 3.12344 9.75156C4.00781 10.6375 5.18438 11.125 6.4375 11.125C7.48438 11.125 8.47813 10.7844 9.29219 10.1562L13.35 14.2125C13.3619 14.2244 13.376 14.2338 13.3916 14.2403C13.4071 14.2467 13.4238 14.2501 13.4406 14.2501C13.4575 14.2501 13.4741 14.2467 13.4897 14.2403C13.5052 14.2338 13.5194 14.2244 13.5312 14.2125L14.2125 13.5328C14.2244 13.5209 14.2338 13.5068 14.2403 13.4912C14.2467 13.4757 14.2501 13.459 14.2501 13.4422C14.2501 13.4254 14.2467 13.4087 14.2403 13.3931C14.2338 13.3776 14.2244 13.3635 14.2125 13.3516ZM8.9125 8.9125C8.25 9.57344 7.37187 9.9375 6.4375 9.9375C5.50312 9.9375 4.625 9.57344 3.9625 8.9125C3.30156 8.25 2.9375 7.37187 2.9375 6.4375C2.9375 5.50312 3.30156 4.62344 3.9625 3.9625C4.625 3.30156 5.50312 2.9375 6.4375 2.9375C7.37187 2.9375 8.25156 3.3 8.9125 3.9625C9.57344 4.625 9.9375 5.50312 9.9375 6.4375C9.9375 7.37187 9.57344 8.25156 8.9125 8.9125Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>

          {/* Nav links */}
          {menuTree
            .filter((item) => ['hoi-dap', 'lien-he', 'so-do-cong'].includes(item?.slug))
            .map((data) => (
              <a
                key={data?.id}
                href={`/${data.slug}`}
                className="text-white/90 hover:text-white hover:underline transition-opacity duration-150 text-[13px]"
              >
                {data?.name}
              </a>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Header;